import { PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useMemo, useState } from "react";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import { useGetMetadocsForFileQuery } from "services/metadoc-api";

type UseMetadocTableConfig = {
  fileId?: number;
  skipCondition?: boolean;
  searchTerm?: string;
};

const isActiveCloneStatus = (status: string | undefined) => {
  const normalizedStatus = status?.toUpperCase().replace(/\s+/g, "_");
  return normalizedStatus === "PENDING" || normalizedStatus === "IN_PROGRESS";
};

/**
 * hook for metadoc table with pagination
 *
 * fetches metadocs filtered by file_id with pagination support.
 */
export const useMetadocTable = (config: UseMetadocTableConfig) => {
  const { fileId, skipCondition, searchTerm } = config;

  const [page, setPage] = useState(1);

  const shouldSkip = skipCondition ?? false;
  const [clonePollBaselineCount, setClonePollBaselineCount] = useState<
    number | null
  >(null);
  const [pauseClonePolling, setPauseClonePolling] = useState(false);
  const [hasSeenActiveCloneJob, setHasSeenActiveCloneJob] = useState(false);

  const { data: backgroundJobsData } = useGetBackgroundJobsQuery(
    {
      filters: {
        file_id: fileId?.toString() || "",
        limit: 50,
        offset: 0,
      },
    },
    {
      skip: !fileId || shouldSkip,
      pollingInterval: hasSeenActiveCloneJob && !pauseClonePolling ? 5000 : 0,
      refetchOnMountOrArgChange: true,
    },
  );

  const hasActiveCloneJob = useMemo(
    () =>
      Boolean(
        backgroundJobsData?.results?.some(
          (job) =>
            job.job_type === "Metadoc Clone" && isActiveCloneStatus(job.status),
        ),
      ),
    [backgroundJobsData],
  );
  const activeCloneMetadocIds = useMemo(
    () =>
      new Set(
        (backgroundJobsData?.results ?? [])
          .filter(
            (job) =>
              job.job_type === "Metadoc Clone" &&
              isActiveCloneStatus(job.status) &&
              typeof job.metadoc_id === "number",
          )
          .map((job) => job.metadoc_id as number),
      ),
    [backgroundJobsData],
  );

  const {
    data = { results: [], count: 0 },
    isLoading,
    isError,
    error,
    isFetching,
  } = useGetMetadocsForFileQuery(
    {
      fileId: fileId?.toString() || "",
      limit: PAGE_SIZE.toString(),
      offset: ((page - 1) * PAGE_SIZE).toString(),
      searchTerm: searchTerm || undefined,
    },
    {
      skip: !fileId || shouldSkip,
      refetchOnMountOrArgChange: true,
      pollingInterval: hasActiveCloneJob && !pauseClonePolling ? 5000 : 0,
    },
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (hasActiveCloneJob) {
      setHasSeenActiveCloneJob(true);
    }
  }, [hasActiveCloneJob]);

  useEffect(() => {
    if (!hasActiveCloneJob) {
      setClonePollBaselineCount(null);
      setPauseClonePolling(false);
      setHasSeenActiveCloneJob(false);
      return;
    }

    if (clonePollBaselineCount === null) {
      setClonePollBaselineCount(data.count);
      return;
    }

    // stop metadoc-list polling once a new metadoc is visible.
    if (data.count > clonePollBaselineCount) {
      setPauseClonePolling(true);
    }
  }, [clonePollBaselineCount, data.count, hasActiveCloneJob]);

  return {
    metadocs: data.results,
    totalCount: data.count,
    isLoading,
    isError,
    error,
    isFetching,
    page,
    setPage,
    activeCloneMetadocIds,
  };
};
